import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Wifi, WifiOff, Loader2 } from 'lucide-react'
import { usePWA } from '../../contexts/PWAContext'

export const ConnectionStatus: React.FC = () => {
  const { isOnline, connectionSpeed } = usePWA()
  const [showStatus, setShowStatus] = React.useState(false)
  const [wasOffline, setWasOffline] = React.useState(false)

  React.useEffect(() => {
    if (!isOnline && !wasOffline) {
      setWasOffline(true)
      setShowStatus(true)
    } else if (isOnline && wasOffline) {
      setShowStatus(true)
      const timer = setTimeout(() => {
        setShowStatus(false)
        setWasOffline(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isOnline, wasOffline])

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        icon: WifiOff,
        message: 'You\'re offline',
        detail: 'Some features may not work',
        bgColor: 'bg-red-500',
        iconColor: 'text-red-100'
      }
    }

    if (connectionSpeed === 'slow') {
      return {
        icon: Loader2,
        message: 'Slow connection',
        detail: 'Responses may take longer',
        bgColor: 'bg-orange-500',
        iconColor: 'text-orange-100 animate-spin'
      }
    }

    return {
      icon: Wifi,
      message: 'Back online',
      detail: 'All features are available',
      bgColor: 'bg-green-500',
      iconColor: 'text-green-100'
    }
  }

  const { icon: Icon, message, detail, bgColor, iconColor } = getStatusConfig()

  return (
    <AnimatePresence>
      {showStatus && (
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          className="fixed top-20 left-4 z-40 max-w-xs"
        >
          <div className={`${bgColor} rounded-lg p-3 shadow-lg backdrop-blur-sm`}>
            <div className="flex items-center gap-2">
              <Icon className={`h-4 w-4 ${iconColor}`} />
              <div>
                <p className="text-sm font-medium text-white">{message}</p>
                <p className="text-xs text-white/80">{detail}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Persistent connection indicator for the header/navbar
export const ConnectionIndicator: React.FC = () => {
  const { isOnline, connectionSpeed } = usePWA()

  const getIndicatorClass = () => {
    if (!isOnline) return 'bg-red-500'
    if (connectionSpeed === 'slow') return 'bg-orange-500'
    return 'bg-green-500'
  }

  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${getIndicatorClass()}`} />
      <span className="text-xs text-gray-500">
        {!isOnline ? 'Offline' : connectionSpeed === 'slow' ? 'Slow' : 'Online'}
      </span>
    </div>
  )
}